import { useWalletSetupOptional } from "@/context/WalletSetupProvider";
import { UserRole, UserType } from "@/types";
import { ArrowLeftStartOnRectangleIcon, Cog8ToothIcon, LanguageIcon, MoonIcon, PaintBrushIcon, SunIcon, UserGroupIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useCompleteSignOut } from "@/hooks/useCompleteSignOut";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { propsProfile } from "./types";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from "@/components/ui/dropdown-menu";
import { typeDropdownLink, typeSublink } from "@/types";
import { AvatarComponent } from "@/components/display";
import { FramerDiv } from "@/components/motion";
import { Theme, useTheme } from "@/context/ThemeProvider";
import { JSX } from "react";
import { cn } from "@/lib/utils";

/**
 * The `Profile` component renders a user profile dropdown menu with user details,
 * theme selection, language settings, and logout functionality.
 *
 * @param {propsProfile} props - The props for the component.
 * @param {object} props.company - The company information associated with the user.
 * @param {object} props.user - The user information to display in the profile.
 * @param {string} props.user.firstname - The first name of the user.
 * @param {string} props.user.lastname - The last name of the user.
 * @param {UserRole} props.user.role - The role of the user.
 * @param {UserType} props.user.usertype - The type of the user.
 *
 * @returns {JSX.Element} The rendered Profile dropdown menu component.
 *
 * @example
 * // Sample usage in a parent component:
 * import { Profile } from './Profile';
 *
 * const company = { name: 'Tech Co.', logo: '/images/company-logo.png' };
 * const user = {
 *   firstname: 'John',
 *   lastname: 'Doe',
 *   role: UserRole.Superuser,
 *   usertype: UserType.Developer
 * };
 *
 * function App() {
 *   return (
 *     <div>
 *       <Profile company={company} user={user} />
 *     </div>
 *   );
 * }
 */
const Profile = ({ entity, user }: propsProfile): JSX.Element => {

  const userName = `${user.firstname} ${user.lastname}`;

  const { darkMode, setTheme, theme } = useTheme();
  const completeSignOut = useCompleteSignOut();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation(["components"]);
  const walletSetup = useWalletSetupOptional();
  const lockNavigation = walletSetup?.locked ?? false;
  const currentLanguage = i18n.language;
  const darkModeClass = darkMode ? "text-white" : "text-gray-600";
  const backgroundColour = darkMode ? "bg-mfdarklighter text-white border-transparent shadow-gray-900" : "bg-white";
  const hoverColour = darkMode ? "hover:bg-white/20" : "hover:bg-gray-100";
  const dropdownLinkClass = "size-6 mr-2 text-gray-600 active:scale-95 dark:text-white shrink-0";

  const languages: typeSublink[] = [
    { name: t("header.languages.en"), code: "en", icon: "🇬🇧" },
    { name: t("header.languages.de"), code: "de", icon: "🇩🇪" },
  ];

  const themes: typeSublink[] = [
    { name: "Dark", icon: <MoonIcon className={cn(dropdownLinkClass, darkModeClass, "mr-[-2px]")} /> },
    { name: "Light", icon: <SunIcon className={cn(dropdownLinkClass, darkModeClass, "mr-[-2px]")} /> },
    { name: "System", icon: <WrenchScrewdriverIcon className={cn(dropdownLinkClass, darkModeClass, "mr-[-2px]")} /> },
  ];

  const handleSignoutClick = () => {
    void completeSignOut();
  };

  const dropdownLinksAlt: typeDropdownLink[] = [
    { name: t("header.dropdownlinks.language"), link: "", type: "Both", icon: <LanguageIcon className={cn(dropdownLinkClass, darkModeClass)} />, sublinks: languages },
    { name: t("header.dropdownlinks.darkmode"), link: "", type: "Both", icon: <PaintBrushIcon className={cn(dropdownLinkClass, darkModeClass)} />, sublinks: themes },
    { name: t("header.navlinks.manageusers"), link: user.userType === UserType.Developer ? "/admin/manageusers" : "/user/manageusers", visibility: [UserType.Developer, UserRole.Superuser, UserRole.Admin], type: "Both", icon: <UserGroupIcon className={cn(dropdownLinkClass, darkModeClass, "mr-[2px]")} /> },
    { name: t("header.dropdownlinks.settings"), link: "/user/settings", type: "Both", icon: <Cog8ToothIcon className={cn(dropdownLinkClass, darkModeClass, "mr-[2px]")} /> },
    { name: t("header.dropdownlinks.signout"), link: "/login", type: "Both", icon: <ArrowLeftStartOnRectangleIcon className={cn(dropdownLinkClass, darkModeClass, "mr-[2px]")} />, onClick: handleSignoutClick },
  ];

  /**
   * Handles the active link styling based on the provided link data and sublink.
   *
   * @param {typeDropdownLink} linkData - The data for the dropdown link, containing its name.
   * @param {typeSublink} sublink - The sublink object, which includes its code and name.
   * @returns {string} A string representing the CSS classes for active styling, or an empty string if not active.
   *
   * @remarks
   * - If the link corresponds to the language option, it checks if the sublink's code matches the current language.
   * - If the link corresponds to the dark mode option, it checks if the sublink's name matches the current theme.
   */
  const handleActiveLink = (linkData: typeDropdownLink, sublink: typeSublink): string => {
    if (linkData.name === t("header.dropdownlinks.language")) {
      return sublink.code === currentLanguage ? !darkMode ? "bg-gray-300" : "bg-gray-500" : "";
    }
    if (linkData.name === t("header.dropdownlinks.darkmode")) {
      return sublink.name.toLowerCase() === theme ? !darkMode ? "bg-gray-300" : "bg-gray-500" : "";
    }
    return "";
  };

  /**
   * Handles the click event for submenu items in the profile dropdown.
   *
   * @param {typeDropdownLink} linkData - The data associated with the clicked link, containing its name.
   * @param {typeSublink} sublink - The data associated with the clicked sublink, containing its name.
   *
   * This function performs the following actions:
   * - Changes the application language if the clicked link corresponds to the language option.
   * - Updates the theme to dark or light mode based on the clicked sublink and user preferences.
   * - Dispatches a 'languagechange' event when the language is changed.
   * - Stores the user's dark mode preference in local storage.
   */
  const handleSubmenuOnClick = (linkData: typeDropdownLink, sublink: typeSublink) => {
    if (linkData.name === t("header.dropdownlinks.language")) {
      const selectedLang = languages?.find(lang => lang.name === sublink.name);
      if (selectedLang) {
        i18n.changeLanguage(selectedLang.code); // Change language
        window.dispatchEvent(new Event("languagechange"));
      }
    } else if (linkData.name === t("header.dropdownlinks.darkmode")) {
      setTheme(sublink.name.toLowerCase() as Theme);
    }
  };

  /**
   * UserInfo component displays user information including the user's name and company.
   *
   * @param {Object} props - The component props.
   * @param {string} props.userName - The name of the user. If not provided, the component returns null.
   * @param {string} [props.companyName] - The name of the company. If not provided, defaults to a developer label.
   * @param {boolean} [props.dropdownOnly] - Determines the visibility of the component based on screen size.
   *
   * @returns {JSX.Element | null} The rendered component or null if userName is not provided.
   */
  const UserInfo = ({ userName, companyName, dropdownOnly }: { userName: string, companyName?: string, dropdownOnly?: boolean }): JSX.Element | null => {
    const companyNameClass = "flex mr-2 text-gray-600 dark:text-white uppercase text-[0.65rem]";
    if (!userName) return null;
    return (
      <div className={cn(dropdownOnly ? "block xl:hidden" : "hidden xl:block ml-2")}>
        <p className={cn("flex mr-2 text-gray-600 dark:text-white uppercase text-xs", darkModeClass)}>
          {`${userName}`}
        </p>
        {
          companyName ?
            <p className={cn(companyNameClass, darkModeClass)}>
              {companyName}
            </p>
            :
            <p className={cn(companyNameClass, darkModeClass)}>
              {t("header.developer")}
            </p>
        }
      </div>
    );
  };

  /**
   * Determines whether a link should be rendered based on the user's role and type.
   */
  const shouldRenderLink = (linkData: typeDropdownLink, user: { userRole: string; userType: string }) => {
    const hasValidVisibility = !linkData.visibility || linkData.visibility.includes(user.userRole) || linkData.visibility.includes(user.userType);
    const hasValidType = linkData.type.includes(user.userRole) || linkData.type.includes(user.userType) || linkData.type === "Both";
    return hasValidVisibility && hasValidType;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Profile menu"
        className={cn("focus:outline-hidden rounded", hoverColour)}
      >
        <FramerDiv
          style={{ borderColor: darkMode ? "rgba(75, 85, 99, 1)" : "rgba(209, 213, 219, 1)" }}
          whileHover={{ borderColor: "rgba(238, 83, 0, 1)" }}
          className="h-9 pr-2 xl:pr-0 focus:outline-hidden cursor-pointer flex items-center xl:min-w-50 border
                    rounded data-[state=open]:border-mforange xl:border shrink-0 relative"
          whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
        >
          <AvatarComponent size="28" userName={userName} logo={entity?.logo} />
          <UserInfo userName={userName} companyName={entity?.name} />
        </FramerDiv>
      </DropdownMenuTrigger>

      {/* Menu content */}
      <DropdownMenuContent
        align="end"
        avoidCollisions
        className={cn("select-none rounded min-w-[200px] py-2 shadow-md border-0 pb-4", backgroundColour)}
      >
        <div className="flex xl:hidden gap-2 items-center mb-2 px-2 pt-2">
          <AvatarComponent size="40" userName={userName} logo={entity?.logo} />
          <UserInfo userName={userName} companyName={entity?.name} dropdownOnly />
        </div>

        {/* Menu items */}
        <DropdownMenuGroup className="px-2">
          {
            dropdownLinksAlt.map((linkData) => (
              shouldRenderLink(linkData, user) && (
                linkData.sublinks ? (
                  // Main dropdown item (trigger for submenu)
                  <DropdownMenuSub key={`dropdown-sub-${linkData.name}`}>
                    <DropdownMenuSubTrigger
                      className={"rounded focus:outline-hidden flex items-center px-2 py-1 transition duration-200 border-0 dark:hover:bg-mforange hover:bg-mforange"}
                    >
                      {linkData.icon}
                      {linkData.name}
                      {
                        linkData.name === t("header.dropdownlinks.language") &&
                        <div className="ml-1">{languages?.find((lang) => lang.code === currentLanguage)?.icon}</div>
                      }
                    </DropdownMenuSubTrigger>

                    {/* Submenu content */}
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent
                        className={cn(
                          "px-2 py-1 rounded focus:outline-hidden min-w-[120px] shadow-md border-0 max-md:data-[state=open]:translate-x-[50px] max-[329px]:data-[state=open]:translate-x-[-50px]",
                          backgroundColour
                        )}
                      >
                        {linkData.sublinks?.map((sublink: typeSublink) => (
                          <DropdownMenuItem
                            key={`sublink-${sublink.name}`}
                            className={cn(
                              "cursor-pointer rounded focus:outline-hidden flex items-center px-2 py-1 transition duration-200 border-0 hover:bg-mforange dark:hover:bg-mforange",
                              handleActiveLink(linkData, sublink)
                            )}
                            onClick={() => handleSubmenuOnClick(linkData, sublink)}
                          >
                            {sublink.icon}
                            <p className="ml-2">
                              {sublink.name}
                            </p>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                ) : ( // ***
                  <DropdownMenuItem
                    key={`dropdown-${linkData.name}`}
                    className={"focus:outline-hidden rounded flex items-center px-2 py-1 cursor-pointer dark:hover:bg-mforange hover:bg-mforange"}
                    onClick={linkData?.onClick ?? (() => {
                      if (lockNavigation) return;
                      void navigate({ to: linkData.link });
                    })}
                  >
                    <div className="">{linkData.icon}</div>
                    <div className="">{linkData.name}</div>
                  </DropdownMenuItem>
                ))
            ))
          }
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Profile;
